import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Pressable, ScrollView, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { TextInput as RNTextInput } from 'react-native';

import Currency from 'components/currency';
import { TextInput } from 'components/ucl';
import { LogUserErrorSeverity } from 'hooks/log-user-error-message';
import useEffectOnce from 'hooks/use-effect-once';
import { useLocale } from 'state/intl';
import { CURRENCY_PREFIX } from 'state/intl/types';
import { TipAmounts } from 'state/order/hooks/use-cart-tip';
import { primitive } from 'styles/constants/primitives';
import { isWeb } from 'utils/environment';

import { useCheckoutLoadingState } from '../../hooks';
import { useCheckoutValidation } from '../../hooks/use-checkout-validation';
import { BaseCheckoutWidgetProps } from '../../types';

import { useTip } from './use-tip';

const TipList = ScrollView.withConfig({
  keyboardShouldPersistTaps: 'always',
  width: 'full',
  overflow: 'hidden',
  borderLeftRadius: 4,
  borderRightRadius: 4,
  padding: 0,
  marginY: '$2',
});

const PressableTip = Pressable.withConfig<{
  $isSelected: boolean;
  $isFirst?: boolean;
  $isLast?: boolean;
  $isTipLocked?: boolean;
  $isDisabled?: boolean;
}>(p => ({
  borderRightWidth: 1,
  borderLeftWidth: 1,
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderRightColor: primitive.$blackOpacity20,
  borderLeftColor: primitive.$blackOpacity20,
  borderTopColor: primitive.$blackOpacity20,
  borderBottomColor: primitive.$blackOpacity20,
  backgroundColor: p.$isSelected
    ? p.$isDisabled || p.$isTipLocked
      ? primitive.$blackOpacity50
      : primitive.bk.$bbqBrown
    : p.$isDisabled || p.$isTipLocked
    ? primitive.$blackOpacity4
    : primitive.$white,
  padding: '$2.5',
  borderLeftRadius: p.$isFirst ? 4 : 0,
  borderRightRadius: p.$isLast ? 4 : 0,
  height: '$12',
}));

const TipText = Text.withConfig<{
  $isSelected: boolean;
  $isTipLocked?: boolean;
  $isDisabled?: boolean;
}>(p => ({
  textAlign: 'center',
  color:
    p.$isTipLocked || p.$isDisabled
      ? p.$isSelected
        ? Styles.color.white
        : primitive.$blackOpacity50
      : p.$isSelected
      ? Styles.color.white
      : primitive.bk.$bbqBrown,
  fontSize: 'md',
  lineHeight: 'lg',
}));

const TipErrorMsg = Box.withConfig({
  marginTop: '$0.5',
  _text: {
    color: Styles.color.error,
    fontSize: '2xs',
  },
});

const MAX_TIP_LENGTH = 6; // Dont allow number longer than $99.99

const DEFAULT_TIP_AMOUNT = '0';

export const TipSelector = ({
  isLoading,
  isDisabled: isExternallyDisabled,
}: BaseCheckoutWidgetProps) => {
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const {
    isDisabled: isInternallyDisabled,
    tipSelection,
    isTipLocked,
    showTipPercentage,
    subTotalCents,
    additionalDiscountAmount,
    tipAmount: currentTipAmount,
    setTipSelection,
    updateTipAmount,
  } = useTip();
  const { isCheckoutLoading } = useCheckoutLoadingState();

  const isDisabled = isExternallyDisabled && isInternallyDisabled;

  const onTipPercentChange = useCallback(
    (tipPercent: string) => {
      updateTipAmount({
        subTotalCents,
        otherDiscountAmount: additionalDiscountAmount,
        newTipOption: 'percent',
        newTipAmount: parseFloat(tipPercent),
      });
    },
    [additionalDiscountAmount, subTotalCents, updateTipAmount]
  );

  const onTipAmountChange = useCallback(
    (newTipAmount: string | undefined) => {
      updateTipAmount({
        subTotalCents,
        otherDiscountAmount: additionalDiscountAmount,
        newTipOption: 'dollar',
        newTipAmount:
          newTipAmount === '' || typeof newTipAmount === 'undefined'
            ? undefined
            : parseFloat(newTipAmount),
      });
    },
    [additionalDiscountAmount, subTotalCents, updateTipAmount]
  );

  const { validate } = useCheckoutValidation();
  const checkoutErrors = validate();
  const errors = checkoutErrors.filter(error => error.placement === 'tipAmount');

  const tip = showTipPercentage ? tipSelection.percentAmount : tipSelection.dollarAmount;
  const [tipString, setTipString] = useState(
    `${CURRENCY_PREFIX[locale]}${tipSelection.otherAmount}`
  );

  const updateOtherTipAmount = (otherAmount: string | undefined) => {
    setTipSelection({
      ...tipSelection,
      isOtherSelected: true,
      otherAmount: otherAmount === '' ? undefined : Number(otherAmount),
    });
    onTipAmountChange(otherAmount);
  };

  const handleTipAmountChange = (tip: string) => {
    const parsedTip = tip.replace(/[^0-9.]/g, '');
    setTipString(`${CURRENCY_PREFIX[locale]}${parsedTip}`);

    // Example: '1.2'
    const tipAmountAsString = parsedTip
      .split('')
      .filter(i => i !== CURRENCY_PREFIX[locale])
      .join('');

    updateOtherTipAmount(tipAmountAsString);
  };

  const onTipChange = useCallback(
    (amount: string) => {
      if (showTipPercentage) {
        onTipPercentChange(amount);
      } else {
        onTipAmountChange(amount);
      }
    },
    [showTipPercentage, onTipAmountChange, onTipPercentChange]
  );

  const buildRadioProps = (tipAmount: string | number) => {
    const numAmount = tipAmount === 'other' ? 0 : tipAmount;
    return {
      $isSelected: tipSelection.isOtherSelected ? tipAmount === 'other' : tip === tipAmount,
      onPress: () => {
        const isOtherSelected = tipAmount === 'other';

        let selection = {
          ...tipSelection,
          isOtherSelected,
          dollarAmount: tipAmount as number,
        };

        if (isDisabled || isTipLocked) {
          return;
        }

        if (tipAmount === 'other') {
          // Reset to default when changing to "Other"
          onTipAmountChange(DEFAULT_TIP_AMOUNT);
          selection = {
            ...tipSelection,
            isOtherSelected,
            otherAmount: undefined,
          };
        }
        if (showTipPercentage) {
          selection = {
            ...tipSelection,
            isOtherSelected,
            percentAmount: tipAmount as number,
          };
        }

        setTipSelection(selection);
        setTipString('');
        if (tipAmount !== 'other') {
          onTipChange(numAmount.toString());
        }
      },
    };
  };

  // Show the error message above the Tip select btns when the tip is > than the subtotal
  // If the user has a invalid tip, keep the error message when the user clicks on the 'other' button until the user start to type in the input
  const showTipSelectBtnError =
    !isLoading && (!tipSelection.isOtherSelected || !tipString) && currentTipAmount > subTotalCents;

  const tipOtherInputRef = useRef<RNTextInput>(null);
  const { isOtherSelected } = tipSelection;
  const previousTipOtherSelected = useRef(isOtherSelected);

  // focus tip input only when "Other" option is first selected
  // prevents keyboard from opening on mobile devices when cart renders Other by default
  useEffect(() => {
    if (!previousTipOtherSelected.current && isOtherSelected && tipOtherInputRef.current) {
      tipOtherInputRef.current.focus();
    }
  }, [isOtherSelected]);

  useEffectOnce(() => {
    if (!isWeb) {
      return;
    }
    // extract tip amount from url,
    // this is used to set the tip amount when the user is redirected from cashapp
    const urlParams = new URLSearchParams(window.location.search);
    const urlTipAmount = urlParams.get('tip');
    const isRedirected = urlParams.get('isRedirected') === 'true';
    const urlTipAmountNumber = isNaN(Number(urlTipAmount)) ? -1 : Number(urlTipAmount);

    if (isRedirected && urlTipAmount !== null) {
      if (urlTipAmountNumber > 0 && tip === urlTipAmountNumber) {
        return;
      }

      const newTipAmount = urlTipAmountNumber / 100;
      setTipSelection({
        ...tipSelection,
        isOtherSelected: true,
        otherAmount: newTipAmount,
      });

      updateTipAmount({
        // this prevents the error from displaying and block the order
        subTotalCents: urlTipAmountNumber + 1,
        otherDiscountAmount: additionalDiscountAmount,
        newTipOption: 'dollar',
        newTipAmount,
      });

      setTipString(`${CURRENCY_PREFIX[locale]}${newTipAmount}`);
    }
  });

  const tipData = showTipPercentage ? TipAmounts.PERCENT_AMOUNTS : TipAmounts.DOLLAR_AMOUNTS;

  return (
    <Box
      background={primitive.$white}
      paddingTop="$5"
      paddingX="$4"
      paddingBottom="$3"
      borderRadius={10}
    >
      <Text variant="headerThree">Delivery Tip</Text>
      <TipList
        borderColor={isTipLocked ? Styles.color.disabledBorder : undefined}
        showsHorizontalScrollIndicator={false}
        horizontal
        accessibilityLabel="Select Tip"
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {tipData.map((amount, index) => {
          return (
            <PressableTip
              key={index}
              flex={1}
              $isTipLocked={isTipLocked}
              $isDisabled={isDisabled || isCheckoutLoading}
              accessibilityLabel={`${amount} tip`}
              {...buildRadioProps(amount)}
              $isFirst={index === 0}
            >
              {showTipPercentage ? (
                <TipText
                  $isDisabled={isDisabled}
                  $isTipLocked={isDisabled}
                  $isSelected={buildRadioProps(amount).$isSelected}
                >{`${amount}%`}</TipText>
              ) : (
                <Currency
                  amount={amount * 100}
                  isLoading={isLoading}
                  textProps={{
                    fontWeight: 'normal',
                    margin: 0,
                    textAlign: 'center',
                    color: buildRadioProps(amount).$isSelected
                      ? primitive.$white
                      : isTipLocked || isDisabled
                      ? primitive.$blackOpacity50
                      : Styles.color.grey.one,
                    fontSize: 'md',
                    lineHeight: 'lg',
                  }}
                />
              )}
            </PressableTip>
          );
        })}
        <PressableTip
          flex={1}
          accessibilityLabel="Other tip"
          {...buildRadioProps('other')}
          $isLast
          $isTipLocked={isTipLocked}
          $isDisabled={isDisabled}
        >
          <TipText
            $isDisabled={isDisabled}
            $isSelected={tipSelection.isOtherSelected}
            $isTipLocked={isTipLocked}
          >
            {formatMessage({ id: 'other' })}
          </TipText>
        </PressableTip>
      </TipList>

      {showTipSelectBtnError && (
        <TipErrorMsg accessibilityRole="alert" testID="showTipSelectBtnError">
          {formatMessage({ id: 'tipAmountMaxError' })}
        </TipErrorMsg>
      )}
      {isOtherSelected && (
        <>
          <Box alignSelf="flex-start" mt="$1">
            <Text fontSize="md" fontWeight="bold" mb="$2" lineHeight="sm">
              Add Tip Amount
            </Text>
          </Box>
          <TextInput
            type="text"
            value={tipString}
            errorMessage={!isCheckoutLoading ? errors[0]?.message ?? undefined : undefined}
            logUserErrorSeverity={LogUserErrorSeverity.Recoverable}
            onChangeText={handleTipAmountChange}
            maxLength={MAX_TIP_LENGTH}
            ref={tipOtherInputRef}
            keyboardType="numeric"
            returnKeyType="done"
            isDisabled={isDisabled || isTipLocked || isCheckoutLoading}
            placeholder="$0.00"
            placeholderTextColor={primitive.$blackOpacity20}
            noMargin
          />
        </>
      )}
    </Box>
  );
};
